import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload,
  Trash2,
  Palette,
  Check,
  X,
  Image as ImageIcon,
  Loader2,
  Eye,
  CheckCircle,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Check if brand assets should be unlocked for editing.
 * - Campaign leads: requires "won" status
 * - One-off/portal builds: requires "approved" status
 */
function isBrandingUnlocked(leadStatus, sourceType) {
  const isOneOff = sourceType === 'one_off' || sourceType === 'portal';
  if (isOneOff) {
    return leadStatus === 'approved';
  }
  return leadStatus === 'won';
}

/**
 * Get the unlock status label for display
 */
function getUnlockStatusLabel(sourceType) {
  const isOneOff = sourceType === 'one_off' || sourceType === 'portal';
  return isOneOff ? 'Approved' : 'Won';
}

export default function BrandAssets({ leadId, leadStatus, sourceType, onUpdate, embedded = false }) {
  const [brandAssets, setBrandAssets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [colors, setColors] = useState({
    brand_primary: "",
    brand_secondary: "",
    brand_accent: "",
    theme_mode_preference: ""
  });
  const fileInputRef = useRef(null);

  const canEdit = isBrandingUnlocked(leadStatus, sourceType);
  const unlockLabel = getUnlockStatusLabel(sourceType);

  useEffect(() => {
    fetchBrandAssets();
  }, [leadId]);

  const fetchBrandAssets = async () => {
    try {
      const res = await axios.get(`${API}/leads/${leadId}/brand-assets`);
      setBrandAssets(res.data);
      setColors({
        brand_primary: res.data.brand_colors?.primary || "",
        brand_secondary: res.data.brand_colors?.secondary || "",
        brand_accent: res.data.brand_colors?.accent || "",
        theme_mode_preference: res.data.brand_colors?.theme_mode_preference || ""
      });
    } catch (error) {
      console.error("Failed to fetch brand assets:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/svg+xml", "image/png", "image/jpeg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload an SVG, PNG, JPG, or WebP file");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum 5MB allowed.");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(`${API}/leads/${leadId}/upload-logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Logo uploaded successfully");
      await fetchBrandAssets();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Failed to upload logo:", error);
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await axios.delete(`${API}/leads/${leadId}/logo`);
      toast.success("Logo deleted");
      await fetchBrandAssets();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Failed to delete logo:", error);
      toast.error("Failed to delete logo");
    }
  };

  const handleColorChange = (field, value) => {
    setColors(prev => ({ ...prev, [field]: value }));
  };

  const saveColors = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/leads/${leadId}/brand-colors`, {
        ...colors,
        brand_colors_confirmed: true
      });
      toast.success("Brand colors saved");
      await fetchBrandAssets();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Failed to save colors:", error);
      toast.error(error.response?.data?.detail || "Failed to save colors");
    } finally {
      setSaving(false);
    }
  };

  const applyPaletteColor = async (colorType, index) => {
    try {
      const applyData = {
        apply_primary: colorType === "primary",
        apply_secondary: colorType === "secondary",
        apply_accent: colorType === "accent",
        primary_index: colorType === "primary" ? index : 0,
        secondary_index: colorType === "secondary" ? index : 1,
        accent_index: colorType === "accent" ? index : 2
      };
      
      await axios.post(`${API}/leads/${leadId}/apply-logo-palette`, applyData);
      toast.success(`Applied as ${colorType} color`);
      await fetchBrandAssets();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Failed to apply palette color:", error);
      toast.error("Failed to apply color");
    }
  };

  if (loading) {
    if (embedded) {
      return (
        <div className="py-4">
          <div className="flex items-center justify-center">
            <Loader2 className="animate-spin" />
          </div>
        </div>
      );
    }
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const logoUrl = brandAssets?.logo?.url 
    ? `${process.env.REACT_APP_BACKEND_URL}${brandAssets.logo.url}`
    : null;

  const content = (
    <div className="space-y-6">
        {/* Logo Section */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Logo</Label>
          
          {logoUrl ? (
            <div className="space-y-3">
              <div className="relative group border rounded-lg p-4 bg-muted/30">
                <img 
                  src={logoUrl} 
                  alt="Uploaded logo" 
                  className="max-h-24 max-w-full object-contain mx-auto"
                />
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button 
                    variant="destructive" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={handleDeleteLogo}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle size={14} className="text-green-500" />
                Logo uploaded {brandAssets?.logo?.uploaded_at && 
                  `on ${new Date(brandAssets.logo.uploaded_at).toLocaleDateString()}`
                }
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div 
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  canEdit ? "hover:border-primary cursor-pointer" : "opacity-50"
                }`}
                onClick={() => canEdit && fileInputRef.current?.click()}
              >
                {uploading ? (
                  <Loader2 className="mx-auto animate-spin text-muted-foreground" />
                ) : (
                  <>
                    <ImageIcon className="mx-auto h-10 w-10 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                      {canEdit ? "Click to upload logo (SVG, PNG, JPG)" : `Mark as ${unlockLabel} to upload logo`}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Max 5MB</p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".svg,.png,.jpg,.jpeg,.webp"
                onChange={handleLogoUpload}
                className="hidden"
                disabled={!canEdit}
              />
            </div>
          )}

          {/* Logo Color Palette */}
          {brandAssets?.logo?.palette_suggested?.length > 0 && (
            <div className="space-y-2 pt-2">
              <Label className="text-xs text-muted-foreground">Suggested Colors from Logo</Label>
              <div className="flex flex-wrap gap-2">
                {brandAssets.logo.palette_suggested.map((color, idx) => (
                  <Dialog key={idx}>
                    <DialogTrigger asChild>
                      <button
                        className="group relative w-10 h-10 rounded-lg border shadow-sm transition-transform hover:scale-110"
                        style={{ backgroundColor: color }}
                        title={color}
                      >
                        <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">
                          {color}
                        </span>
                      </button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Apply Color</DialogTitle>
                        <DialogDescription>
                          Apply {color} as a brand color
                        </DialogDescription>
                      </DialogHeader>
                      <div className="flex items-center gap-4 py-4">
                        <div 
                          className="w-16 h-16 rounded-lg border"
                          style={{ backgroundColor: color }}
                        />
                        <div className="space-y-1">
                          <p className="font-mono text-lg">{color}</p>
                          <p className="text-sm text-muted-foreground">Click a button to apply</p>
                        </div>
                      </div>
                      <DialogFooter className="flex-col sm:flex-row gap-2">
                        <Button 
                          variant="outline" 
                          onClick={() => applyPaletteColor("primary", idx)}
                        >
                          Apply as Primary
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => applyPaletteColor("secondary", idx)}
                        >
                          Apply as Secondary
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => applyPaletteColor("accent", idx)}
                        >
                          Apply as Accent
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Brand Colors Section */}
        <div className="space-y-4 pt-2 border-t">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Brand Colors</Label>
            {!canEdit && (
              <Badge variant="secondary" className="text-xs">
                Available after {unlockLabel}
              </Badge>
            )}
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Primary Color */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Primary *</Label>
              <div className="flex gap-2">
                <div 
                  className="w-10 h-10 rounded border shrink-0"
                  style={{ backgroundColor: colors.brand_primary || "#e5e7eb" }}
                />
                <Input
                  type="text"
                  placeholder="#2563eb"
                  value={colors.brand_primary}
                  onChange={(e) => handleColorChange("brand_primary", e.target.value)}
                  disabled={!canEdit}
                  className="font-mono text-sm"
                />
              </div>
            </div>

            {/* Secondary Color */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Secondary</Label>
              <div className="flex gap-2">
                <div 
                  className="w-10 h-10 rounded border shrink-0"
                  style={{ backgroundColor: colors.brand_secondary || "#e5e7eb" }}
                />
                <Input
                  type="text"
                  placeholder="#1e40af"
                  value={colors.brand_secondary}
                  onChange={(e) => handleColorChange("brand_secondary", e.target.value)}
                  disabled={!canEdit}
                  className="font-mono text-sm"
                />
              </div>
            </div>

            {/* Accent Color */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Accent</Label>
              <div className="flex gap-2">
                <div 
                  className="w-10 h-10 rounded border shrink-0"
                  style={{ backgroundColor: colors.brand_accent || "#e5e7eb" }}
                />
                <Input
                  type="text"
                  placeholder="#fbbf24"
                  value={colors.brand_accent}
                  onChange={(e) => handleColorChange("brand_accent", e.target.value)}
                  disabled={!canEdit}
                  className="font-mono text-sm"
                />
              </div>
            </div>
          </div>

          {/* Theme Mode Preference */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Theme Preference</Label>
            <Select
              value={colors.theme_mode_preference || "no_preference"}
              onValueChange={(value) => handleColorChange("theme_mode_preference", value)}
              disabled={!canEdit}
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Select preference" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no_preference">No Preference</SelectItem>
                <SelectItem value="light">Light Mode</SelectItem>
                <SelectItem value="dark">Dark Mode</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Save Button */}
          {canEdit && (
            <div className="flex justify-end pt-2">
              <Button 
                onClick={saveColors}
                disabled={saving || !colors.brand_primary}
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Save Brand Colors
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Info Notice */}
        <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg text-sm">
          <AlertCircle size={16} className="text-blue-500 mt-0.5 shrink-0" />
          <p className="text-blue-700 dark:text-blue-300">
            Brand colors are only applied during Full Site Build. 
            The homepage preview sent in cold emails uses auto-generated theme colors.
          </p>
        </div>

        {/* Full Site Status (if applicable) */}
        {brandAssets?.full_site?.status && brandAssets.full_site.status !== "none" && (
          <div className="pt-2 border-t">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Full Site Build</Label>
                <p className="text-xs text-muted-foreground">
                  Status: {brandAssets.full_site.status}
                </p>
              </div>
              {brandAssets.full_site.url && (
                <Button variant="outline" size="sm" asChild>
                  <a href={brandAssets.full_site.url} target="_blank" rel="noopener noreferrer">
                    <Eye className="mr-2 h-4 w-4" />
                    View Site
                  </a>
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
  );

  // If embedded, return content without Card wrapper
  if (embedded) {
    return content;
  }

  // Otherwise return full Card
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Palette size={20} />
              Brand Assets
            </CardTitle>
            <CardDescription>
              {canEdit 
                ? "Upload logo and set brand colors for the full site build"
                : `Brand assets are available after marking the lead as ${unlockLabel}`
              }
            </CardDescription>
          </div>
          {brandAssets?.brand_colors?.confirmed && (
            <Badge variant="outline" className="text-green-600 border-green-600">
              <CheckCircle size={12} className="mr-1" />
              Colors Confirmed
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
}
